/**
 * KPI Grid — 6 cards, 3×2. Each card carries a tier accent (primary = mustard,
 * health = sage, watch = slate).
 *
 * Non-negotiable: no data ≠ zero. A card with has_data = false renders "—",
 * never "0". The edit modal makes the two states explicit.
 */

import { useState } from 'react';
import { Pencil } from 'lucide-react';
import type { AssembledPor, PorKpi } from '../../types/plan-of-record';
import { updateKpi } from '../../lib/planOfRecordService';
import { KPI_TIER_ACCENT, PorCard, PorCardHeader, PorModal, GhostButton, PrimaryButton, kpiDisplayValue } from './porUi';

export function KpiGrid({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<PorKpi | null>(null);

  return (
    <PorCard>
      <PorCardHeader title="KPIs" count={assembled.kpis.length} hint="Phase indicators — manual entry until data connects." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {assembled.kpis.map((kpi) => (
          <KpiCard key={kpi.id} kpi={kpi} onEdit={() => setEditing(kpi)} />
        ))}
      </div>

      {editing && (
        <KpiEditModal
          kpi={editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </PorCard>
  );
}

function KpiCard({ kpi, onEdit }: { kpi: PorKpi; onEdit: () => void }) {
  const accent = KPI_TIER_ACCENT[kpi.tier];
  const display = kpiDisplayValue(kpi.has_data, kpi.value_display);
  const noData = !kpi.has_data;

  return (
    <div className="group relative border border-[var(--rule)] p-4 hover:border-[var(--rule)] transition-colors">
      <button
        onClick={onEdit}
        className="absolute top-3 right-3 text-[var(--text-ter)] hover:text-[var(--text-sec)] opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={`Edit ${kpi.label}`}
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>

      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
        <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)]">{kpi.tier}</span>
      </div>
      <p className="text-sm font-medium text-[var(--text-sec)] mt-1 leading-snug">{kpi.label}</p>

      <div className="mt-3 flex items-end gap-2">
        <span className={`text-2xl font-semibold tabular-nums ${noData ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
          {display}
        </span>
        {kpi.target_label && (
          <span className="text-xs text-[var(--text-ter)] mb-1">
            / {kpi.target_label}
            {kpi.target_window ? ` · ${kpi.target_window}` : ''}
          </span>
        )}
      </div>

      {/* Progress bar — only meaningful when there is data */}
      <div className="mt-2 h-1.5 rounded-full bg-[var(--hover-tint)] overflow-hidden">
        {!noData && kpi.progress_pct != null && (
          <div className={`h-full ${accent.bar}`} style={{ width: `${kpi.progress_pct}%` }} />
        )}
      </div>
      {noData && <p className="text-[11px] text-[var(--text-ter)] mt-1.5">No data yet — not zero.</p>}
    </div>
  );
}

function KpiEditModal({
  kpi,
  onClose,
  onDone,
}: {
  kpi: PorKpi;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [hasData, setHasData] = useState(kpi.has_data);
  const [valueDisplay, setValueDisplay] = useState(kpi.value_display ?? '');
  const [targetLabel, setTargetLabel] = useState(kpi.target_label ?? '');
  const [targetWindow, setTargetWindow] = useState(kpi.target_window ?? '');
  const [progress, setProgress] = useState<string>(kpi.progress_pct != null ? String(kpi.progress_pct) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const pct = progress.trim() === '' ? null : Math.max(0, Math.min(100, Number(progress)));
    const { error: err } = await updateKpi(kpi.id, {
      has_data: hasData,
      value_display: hasData ? valueDisplay.trim() || null : null,
      target_label: targetLabel.trim() || null,
      target_window: targetWindow.trim() || null,
      progress_pct: hasData ? pct : null,
    });
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title={kpi.label}
      subtitle="“No data” and “0” are different states. Choose deliberately."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={hasData}
            onChange={(e) => setHasData(e.target.checked)}
            className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-200"
          />
          <span className="text-sm text-[var(--text-sec)]">
            This KPI has data. {hasData ? '' : 'Unchecked → the card shows “—”.'}
          </span>
        </label>

        {hasData && (
          <div>
            <label className={label}>Value (display)</label>
            <input
              value={valueDisplay}
              onChange={(e) => setValueDisplay(e.target.value)}
              placeholder='e.g. "3", "82%", "10/14"'
              className={field}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Target</label>
            <input
              value={targetLabel}
              onChange={(e) => setTargetLabel(e.target.value)}
              placeholder='e.g. "3"'
              className={field}
            />
          </div>
          <div>
            <label className={label}>Target window</label>
            <input
              value={targetWindow}
              onChange={(e) => setTargetWindow(e.target.value)}
              placeholder='e.g. "30d"'
              className={field}
            />
          </div>
        </div>

        {hasData && (
          <div>
            <label className={label}>Progress %</label>
            <input
              type="number"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => setProgress(e.target.value)}
              placeholder="0–100"
              className={field}
            />
          </div>
        )}

        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
